import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  useTheme,
  CircularProgress,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchBranches } from "../../../../../utils/Apis";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// props: open, onClose, onSubmit, mode, courseid (for edit)
const CourseEditModal = ({
  open,
  onClose,
  onSubmit,
  mode = "add",
  courseid,
}) => {
  const dispatch = useDispatch();
  const courses = useSelector((state) => state.data.courses?.courses || []);
  const branches = useSelector((state) => state.data.branches?.branches || []);
  const loading = useSelector((state) => state.data.loading);
  const error = useSelector((state) => state.data.error);
  const theme = useTheme();

  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  const defaultFormData = {
    name: "",
    description: "",
    duration: { value: "", unit: "months" },
    feeStructure: { baseFee: "", discount: "0", finalFee: "" },
    prerequisites: [],
    prerequisitesInput: "",
    availableAtBranches: [],
    coursePic: null,
  };

  const [formData, setFormData] = useState(defaultFormData);

  const existingCourse = courses.find((course) => course._id === courseid);

  // Fetch course details when editing
  useEffect(() => {
    if (mode === "edit" && courseid && open) {
      // First check if we already have the course data in the store

      if (existingCourse) {
        setFormData({
          ...existingCourse,
          coursePic: null,
          prerequisites: existingCourse.prerequisites || [],
          prerequisitesInput: "",
          availableAtBranches: existingCourse.availableAtBranches || [],
        });

        // If there's a course image URL, set it as preview
        if (existingCourse.courseImageUrl) {
          setImagePreview(existingCourse.courseImageUrl);
        } else {
          setImagePreview(null);
        }
      } else {
        // If we don't have it, fetch it from the server
        dispatch({
          type: "FETCH_COURSE_DETAILS_REQUEST",
          payload: courseid,
          onSuccess: (courseData) => {
            setFormData({
              ...courseData,
              coursePic: null,
              prerequisites: courseData.prerequisites || [],
              availableAtBranches: courseData.availableAtBranches || [],
            });

            if (courseData.courseImageUrl) {
              setImagePreview(courseData.courseImageUrl);
            }
          },
        });
      }
    } else if (mode === "add" && open) {
      setFormData(defaultFormData);
      setImagePreview(null);
    }
  }, [courseid, mode, open, dispatch, courses]);

  // Fetch branches if not available
  useEffect(() => {
    if (open && branches.length === 0) {
      dispatch(fetchBranches());
    }
  }, [open, branches.length, dispatch]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Course name is required";
    }

    if (!formData.description.trim()) {
      errors.description = "Description is required";
    }

    if (!formData.duration.value) {
      errors.durationValue = "Duration value is required";
    }

    if (!formData.duration.unit) {
      errors.durationUnit = "Duration unit is required";
    }

    if (!formData.feeStructure.baseFee) {
      errors.baseFee = "Base fee is required";
    }

    if (formData.availableAtBranches.length === 0) {
      errors.branches = "Select at least one branch";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e, path) => {
    const { name, value, type, checked } = e.target;

    // Clear the specific error when field is changed
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }

    if (path === "duration") {
      setFormData((prev) => ({
        ...prev,
        duration: {
          ...prev.duration,
          [name]: value,
        },
      }));

      if (formErrors.durationValue || formErrors.durationUnit) {
        setFormErrors({
          ...formErrors,
          durationValue: name === "value" ? null : formErrors.durationValue,
          durationUnit: name === "unit" ? null : formErrors.durationUnit,
        });
      }
    } else if (path === "feeStructure") {
      const newFeeStructure = {
        ...formData.feeStructure,
        [name]: value,
      };

      // Calculate final fee from base fee and discount
      const base = parseFloat(newFeeStructure.baseFee || 0);
      const discount = parseFloat(newFeeStructure.discount || 0);
      newFeeStructure.finalFee = Math.max(0, base - discount).toFixed(2);

      setFormData((prev) => ({
        ...prev,
        feeStructure: newFeeStructure,
      }));

      if (formErrors.baseFee && name === "baseFee") {
        setFormErrors({ ...formErrors, baseFee: null });
      }
    } else if (path === "branches") {
      setFormData((prev) => ({
        ...prev,
        availableAtBranches:
          typeof value === "string" ? value.split(",") : value,
      }));

      if (formErrors.branches) {
        setFormErrors({ ...formErrors, branches: null });
      }
    } else if (path === "prerequisites") {
      const prerequisites = value
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "");

      setFormData((prev) => ({
        ...prev,
        prerequisites,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handlePrerequisiteInput = (e) => {
  setFormData((prev) => ({
    ...prev,
    prerequisitesInput: e.target.value,
  }));
};

const handlePrerequisiteKeyPress = (e) => {
  if (e.key === "," || e.key === "Enter") {
    e.preventDefault();
    const value = formData.prerequisitesInput.trim().replace(",", "");

    if (value) {
      setFormData((prev) => ({
        ...prev,
        prerequisites: [...prev.prerequisites, value],
        prerequisitesInput: "",
      }));
    }
  }
};

const handleRemovePrerequisite = (index) => {
  const updated = [...formData.prerequisites];
  updated.splice(index, 1);

  setFormData((prev) => ({
    ...prev,
    prerequisites: updated,
  }));
};

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        setFormErrors({
          ...formErrors,
          image: "File size must be less than 2MB",
        });
        return;
      }

      // Preview the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setFormData((prev) => ({
        ...prev,
        coursePic: file,
      }));

      if (formErrors.image) {
        setFormErrors({ ...formErrors, image: null });
      }
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      // Create a new object for submission to avoid unnecessary fields
      const submissionData = {
        ...formData,
        id: mode === "edit" ? courseid : undefined, // Include ID only for edit mode
      };

      onSubmit(submissionData);
      onClose();
    }
  };

  // Find branch names from IDs
  const getBranchNameById = (branchId) => {
    const branch = branches.find((b) => b._id === branchId);
    return branch ? branch.name : branchId;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      borderRadius={10}
      PaperProps={{
        sx: {
          borderRadius: theme.shape.borderRadius,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {mode === "edit" ? "Edit Course Details" : "Add New Course"}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TextField
              label="Course Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              error={!!formErrors.name}
              helperText={formErrors.name}
              required
            />

            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
              error={!!formErrors.description}
              helperText={formErrors.description}
              required
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Duration"
                name="value"
                type="number"
                value={formData.duration.value}
                onChange={(e) => handleChange(e, "duration")}
                fullWidth
                error={!!formErrors.durationValue}
                helperText={formErrors.durationValue}
                required
                InputProps={{ inputProps: { min: 1 } }}
              />
              <TextField
                label="Unit"
                name="unit"
                value={formData.duration.unit}
                onChange={(e) => handleChange(e, "duration")}
                select
                fullWidth
                error={!!formErrors.durationUnit}
                helperText={formErrors.durationUnit}
                required
              >
                <MenuItem value="months">Months</MenuItem>
                <MenuItem value="weeks">Weeks</MenuItem>
                <MenuItem value="days">Days</MenuItem>
              </TextField>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Base Fee"
                name="baseFee"
                type="number"
                value={formData.feeStructure.baseFee}
                onChange={(e) => handleChange(e, "feeStructure")}
                fullWidth
                error={!!formErrors.baseFee}
                helperText={formErrors.baseFee}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label="Discount"
                name="discount"
                type="number"
                value={formData.feeStructure.discount}
                onChange={(e) => handleChange(e, "feeStructure")}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label="Final Fee"
                name="finalFee"
                type="number"
                value={formData.feeStructure.finalFee}
                disabled
                fullWidth
              />
            </Box>

            <Box>
  <Typography fontWeight="500" mb={1}>
    Prerequisites
  </Typography>

  <Box
    sx={{
      minHeight: 56,
      border: "1px solid #ccc",
      borderRadius: 1,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      padding: "6px 8px",
      gap: 1,
    }}
  >
    {/* Show chips */}
    {formData.prerequisites.map((item, index) => (
      <Chip
        key={index}
        label={item}
        onDelete={() => handleRemovePrerequisite(index)}
        color="primary"
        variant="outlined"
      />
    ))}

    {/* Input box for adding new prerequisites */}
    <input
      style={{
        flexGrow: 1,
        border: "none",
        outline: "none",
        minWidth: 120,
        fontSize: "16px",
      }}
      value={formData.prerequisitesInput}
      onChange={handlePrerequisiteInput}
      onKeyDown={handlePrerequisiteKeyPress}
      placeholder="Type and press comma..."
    />
  </Box>
</Box>


            <FormControl fullWidth error={!!formErrors.branches} required>
              <InputLabel id="branch-select-label">
                Available At Branches
              </InputLabel>
              <Select
                labelId="branch-select-label"
                multiple
                value={formData.availableAtBranches}
                onChange={(e) => handleChange(e, "branches")}
                input={<OutlinedInput label="Available At Branches" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((branchId) => (
                      <Chip
                        key={branchId}
                        label={getBranchNameById(branchId)}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {branches.length === 0 ? (
                  <MenuItem disabled>Loading branches...</MenuItem>
                ) : mode === "edit" ? (
                  existingCourse?.availableAtBranches.map((branchId) => {
                    const branch = branches?.find((b) => b._id === branchId);
                    return (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.name || branchId}
                      </MenuItem>
                    );
                  })
                ) : (
                  branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {formErrors.branches && (
                <FormHelperText>{formErrors.branches}</FormHelperText>
              )}
            </FormControl>

            <Box>
              <Button
                variant="outlined"
                component="label"
                color={formErrors.image ? "error" : "primary"}
              >
                Upload Course Picture
                <input
                  type="file"
                  accept="image/*"
                  name="uploaded_file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {formErrors.image && (
                <FormHelperText error>{formErrors.image}</FormHelperText>
              )}

              {imagePreview && (
                <Box
                  mt={2}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    gutterBottom
                  >
                    Preview:
                  </Typography>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Course preview"
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: 1,
                    }}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : mode === "edit" ? (
            "Save Changes"
          ) : (
            "Add Course"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseEditModal;
